import React, { useEffect, useState } from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import "./MapTemplate.css";
import MapNavbar from "../../components/organisms/mapNavbar/MapNavbar";
import NewAnalysisSidebar from "../../components/organisms/newAnalysisSidebar/NewAnalysisSidebar";
import LayersSidebar from "../../components/organisms/layersSidebar/LayersSidebar";
import MapToolbar from "../../components/organisms/mapToolbar/MapToolbar";
import HistoricFiresModal from "../../components/molecules/historicFiresModal/HistoricFiresModal.jsx";
import UserHistoricSidebar from "../../components/organisms/userHistoric/UserHistoricSidebar.jsx";
import ImpactLegend from "../../components/molecules/impactLegend/ImpactLegend";
import ViewedBanner from "../../components/molecules/viewedBanner/ViewedBanner.jsx";
import { zoomToFeature } from "../../utils/zoomToFeature.js";
import useMapbox from "../../hooks/mapTemplate/useMapbox.js";
import useLegend from "../../hooks/mapTemplate/useLegend.js";
import useMapSync from "../../hooks/mapTemplate/useMapSync.js";
import usePolygonInput from "../../hooks/mapTemplate/usePolygonInput.js";
import usePanelResize from "../../hooks/mapTemplate/usePanelResize.js";

const MapTemplate = ({
  mapContainerRef,
  mapRef,
  logout,
  biomass,
  onShowBiomass,
  forest,
  onShowForest,
  grassland,
  onShowGrassland,
  shrubland,
  onShowShrubland,
  buildings,
  onShowBuildings,
  relief,
  toggleRelief,
  historicFires,
  onFetchHistoricFires,
  onSelectHistoricFire,
  realPolygon,
  setRealPolygon,
  simPolygon,
  setSimPolygon,
  onLaunchAnalysis,
  impacts,
  onLaunchCost,
  cost,
  report,
  onLaunchReport,
  onResetAnalysis,
  stats,
  onFetchStats,
  statsIds,
  onFetchStatsByIds,
  statsCandidates,
  onFetchStatsCandidates,
  analyses,
  onFetchAnalyses,
  onDeleteAnalysis,
  reportPreview,
  onOpenReportPreview,
  onCloseReportPreview,
  openingId,
  onOpenAnalysis,
  viewed,
  onCloseViewed,
}) => {
  /* ------------------------- which panel is open ------------------------- */
  const [activeItem, setActiveItem] = useState(null); // "new" | "analyses" | "layers" | null
  const isAnalysisOpen = activeItem === "new";
  const isHistoryOpen = activeItem === "analyses";
  const [historicOpen, setHistoricOpen] = useState(false);

  const handleSelectItem = (id) =>
    setActiveItem((current) => (current === id ? null : id));
  const closePanel = () => setActiveItem(null);

  /* ------------------------------- hooks -------------------------------- */
  const {
    mapReady,
    styleVersion,
    styleLoading,
    selectedStyle,
    handleStyleChange,
  } = useMapbox(mapRef, mapContainerRef);

  const {
    legend,
    legendOpen,
    toggleLegend,
    impactVisible,
    impactVisibleRef,
    toggleImpact,
  } = useLegend({ impacts, viewed, realPolygon, simPolygon });

  useMapSync({
    mapRef,
    mapReady,
    styleVersion,
    styleLoading,
    realPolygon,
    simPolygon,
    impacts,
    viewed,
    relief,
    impactVisible,
    impactVisibleRef,
  });

  const zoomTo = (feature) => zoomToFeature(mapRef.current, feature);

  const { fileInputRef, handleImport, handleFileChange, handleDraw, stopDraw } =
    usePolygonInput({
      mapRef,
      realPolygon,
      simPolygon,
      setRealPolygon,
      setSimPolygon,
      onImported: zoomTo,
    });

  const { workspaceRef, panelWidth, resizing, startResize, resetPanelWidth } =
    usePanelResize();

  // load the stats each time the history panel opens (only on open, on purpose)
  useEffect(() => {
    if (isHistoryOpen) onFetchStats();
  }, [isHistoryOpen]);

  /* ------------------------------ handlers ------------------------------ */
  const handleOpenHistoric = () => {
    setHistoricOpen(true);
    // load the fires list the first time (or again after a failed load)
    if (!historicFires?.data?.length) onFetchHistoricFires();
  };

  const handleSelectHistoric = async (fire) => {
    setHistoricOpen(false);
    const feature = await onSelectHistoricFire(fire);
    if (feature) zoomTo(feature);
    else alert("Impossible de charger le polygone de ce feu.");
  };

  const handleOpenAnalysis = async (item) => {
    const shapes = await onOpenAnalysis(item);
    if (shapes)
      zoomTo(shapes); // bbox accepts a FeatureCollection
    else alert("Impossible d’ouvrir cette analyse.");
  };

  const handleResetAnalysis = () => {
    stopDraw();
    onResetAnalysis();
  };

  /* ---------------------------- layers panel ---------------------------- */
  // no useMemo: onShowBuildings / toggleRelief are new functions on each render,
  // so the memo would recompute every time anyway (and building this array is cheap)
  // To enable a layer "à venir": uncomment it here AND its props in the destructuring above.
  const layers = [
    {
      id: "forest",
      title: "Forêt",
      onChange: onShowForest,
      showItem: forest.show,
      disabled: false,
      classes: [
        { label: "Pin", color: "#B69AF5" },
        { label: "Chêne", color: "#FFB3EE" },
        { label: "Conifères", color: "#76F5A4" },
        { label: "Feuillus", color: "#65920B" },
        { label: "Forêt mixte", color: "#0CEB31" },
      ],
    },
    {
      id: "biomass",
      title: "Biomasse",
      onChange: onShowBiomass,
      showItem: biomass.show,
      disabled: false,
      gradient: {
        stops: [
          { color: "#F5B46F", label: "1" },
          { color: "#DB804C", label: "4" },
          { color: "#EB6213", label: "8" },
          { color: "#D43D06", label: "14" },
          { color: "#77351D", label: "20+" },
        ],
      },
    },
    {
      id: "terrain3d",
      group: "relief",
      title: "Topographie 3D",
      onChange: () => toggleRelief("terrain"),
      showItem: relief.terrain,
    },
    {
      id: "buildings3d",
      group: "relief",
      title: "Bâtiments 3D",
      onChange: () => toggleRelief("buildings3d"),
      showItem: relief.buildings3d,
    },
    {
      id: "buildings",
      title: "Zone urbaine",
      onChange: onShowBuildings,
      showItem: buildings.show,
      disabled: false,
      classes: [{ label: "Bâtiment", color: "#888a8a" }],
    },
    {
      id: "grassland",
      title: "Strate herbacée",
      onChange: onShowGrassland,
      showItem: grassland.show,
      disabled: false,
      classes: [{ label: "Strate herbacée", color: "#9ACD32" }],
    },
    {
      id: "shrubland",
      title: "Strate arbustive",
      onChange: onShowShrubland,
      showItem: shrubland.show,
      disabled: false,
      classes: [{ label: "Strate arbustive", color: "#D9D925" }],
    },
  ];

  /* -------------------------------- render ------------------------------ */
  return (
    <div className="app-view">
      <input
        ref={fileInputRef}
        type="file"
        accept=".geojson,.json,.kml"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
      <MapNavbar
        activeItem={activeItem}
        onSelectItem={handleSelectItem}
        onProfile={() => console.log("profile")}
        logout={logout}
        selectedStyle={selectedStyle}
        onStyleChange={handleStyleChange}
      />

      <div
        ref={workspaceRef}
        className={`map-workspace ${resizing ? "is-resizing" : ""}`}
        style={panelWidth ? { "--analysis-w": `${panelWidth}px` } : undefined}
      >
        <div
          ref={mapContainerRef}
          className="map-container"
          data-testid="map-container"
        >
          {mapReady && (
            <MapToolbar
              mapRef={mapRef}
              selectedStyle={selectedStyle}
              onStyleChange={handleStyleChange}
              legendOpen={legendOpen}
              onToggleLegend={toggleLegend}
              legendAvailable={!!legend}
            />
          )}
          {legend && legendOpen && (
            <ImpactLegend
              results={legend.results}
              realArea={legend.realArea}
              simArea={legend.simArea}
              showAreas={legend.showAreas}
              visible={impactVisible}
              onToggle={toggleImpact}
            />
          )}
          {viewed && (
            <ViewedBanner
              reference={viewed.item.reference}
              onClose={onCloseViewed}
            />
          )}
          <HistoricFiresModal
            open={historicOpen}
            onClose={() => setHistoricOpen(false)}
            fires={historicFires}
            selectedId={realPolygon.fireId}
            onSelect={handleSelectHistoric}
          />
        </div>

        <LayersSidebar
          layers={layers}
          open={activeItem === "layers"}
          onClose={closePanel}
        />

        {(isAnalysisOpen || isHistoryOpen) && (
          <div
            className="panel-resizer"
            onPointerDown={startResize}
            onDoubleClick={resetPanelWidth}
            title="Glisser pour redimensionner"
          />
        )}

        <aside
          className={`analysis-panel ${isAnalysisOpen ? "is-open" : ""}`}
          aria-hidden={!isAnalysisOpen}
          {...(!isAnalysisOpen ? { inert: "" } : {})}
        >
          <div className="analysis-panel__inner">
            <NewAnalysisSidebar
              realPolygon={realPolygon}
              simPolygon={simPolygon}
              onDraw={handleDraw}
              onImport={handleImport}
              onSelectHistoric={handleOpenHistoric}
              onLaunchAnalysis={onLaunchAnalysis}
              impacts={impacts}
              onClose={closePanel}
              onLaunchCost={onLaunchCost}
              cost={cost}
              report={report}
              onLaunchReport={onLaunchReport}
              onReset={handleResetAnalysis}
            />
          </div>
        </aside>

        <aside
          className={`analysis-panel ${isHistoryOpen ? "is-open" : ""}`}
          aria-hidden={!isHistoryOpen}
          {...(!isHistoryOpen ? { inert: "" } : {})}
        >
          <div className="analysis-panel__inner">
            <UserHistoricSidebar
              onClose={closePanel}
              stats={stats}
              onRetry={onFetchStats}
              statsIds={statsIds}
              onFetchStatsByIds={onFetchStatsByIds}
              statsCandidates={statsCandidates}
              onFetchStatsCandidates={onFetchStatsCandidates}
              analyses={analyses}
              onFetchAnalyses={onFetchAnalyses}
              reportPreview={reportPreview}
              onOpenReportPreview={onOpenReportPreview}
              onCloseReportPreview={onCloseReportPreview}
              onOpenAnalysis={handleOpenAnalysis}
              openingId={openingId}
              onDelete={onDeleteAnalysis}
              viewedId={viewed?.item?.analysis_id}
              onCloseViewed={onCloseViewed}
            />
          </div>
        </aside>
      </div>
    </div>
  );
};

export default MapTemplate;
